import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, LinearProgress } from '@mui/material'
import { capitalize } from '../../../utils/functions'
import { ui, tone, Tone } from '../../../utils/uiTokens'
import { IDLE_ALERT_DAYS, StudentEvent, studentActivityApi } from '../../../features/curator/studentActivity'

const PERIODS = [7, 14, 30]

const TYPE_LABEL: Record<string, string> = {
    VIDEO: 'video',
    TEST: 'topicTest',
    CONSPECT: 'conspect',
    HOMEWORK: 'homework',
    PRACTICE: 'practice',
    ZACHET: 'zachet',
}

const PRACTICE_LABEL: Record<string, string> = {
    WITH_CAMERA: 'withCamera',
    WITHOUT_CAMERA: 'withoutCamera',
    ABSENT: 'absent',
}

// yyyy-MM-dd... -> dd.MM.yyyy
const formatDate = (s: string) => s.slice(0, 10).split('-').reverse().join('.')
const formatTime = (s?: string | null) => (s ? s.slice(11, 16) : '')

const chip = (t: { text: string; bg: string; border: string }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: '24px',
    fontSize: '12.5px',
    fontWeight: 600,
    color: t.text,
    backgroundColor: t.bg,
    border: `1px solid ${t.border}`,
    whiteSpace: 'nowrap' as const,
})

interface Props {
    userCourseEnrollId: number
}

type Detail = { text: string; tone?: Tone }

const keep = (xs: (Detail | null)[]): Detail[] => xs.filter((x): x is Detail => !!x)

// Оқушыны бақылау: что ученик делал на платформе по дням. Область решает бэкенд (403 - блока нет).
const StudentActivityBlock: React.FC<Props> = ({ userCourseEnrollId }) => {
    const { formatMessage, locale } = useIntl()
    const [days, setDays] = useState(14)
    const { data, isFetching, isError } = studentActivityApi.useFetchStudentActivityQuery(
        { userCourseEnrollId, days },
        { skip: !userCourseEnrollId },
    )

    if (isError) return null

    const idle = data?.idleDays
    const alert = data && (idle === null || idle >= IDLE_ALERT_DAYS)
    const weekday = (day: string) =>
        new Date(`${day}T12:00:00`).toLocaleDateString(locale === 'kz' ? 'kk-KZ' : 'ru-RU', { weekday: 'short' })
    const ago = (n: number) =>
        n === 0
            ? formatMessage({ id: 'sxToday' })
            : n === 1
                ? formatMessage({ id: 'sxYesterday' })
                : formatMessage({ id: 'sxDaysAgo' }, { n })

    const subjectOf = (e: StudentEvent) => e[`subjectTitle${capitalize(locale)}`] || e.subjectTitleKz || e.subjectTitleRu
    const practice = (s: string | null) => formatMessage({ id: (s && PRACTICE_LABEL[s]) || 'notMarked' })
    const week = (e: StudentEvent) =>
        e.monthNumber && e.weekNumber ? formatMessage({ id: 'sxWeekLabel' }, { m: e.monthNumber, w: e.weekNumber }) : ''

    // Пояснение к событию: что именно и насколько
    const details = (e: StudentEvent): Detail[] => {
        switch (e.type) {
            case 'VIDEO':
                return keep([
                    e.reachedPercent !== null ? { text: formatMessage({ id: 'sxVideoReached' }, { p: e.reachedPercent }) } : null,
                    e.watchedSeconds ? { text: formatMessage({ id: 'sxMinutes' }, { n: Math.max(1, Math.round(e.watchedSeconds / 60)) }) } : null,
                    e.totalPercent !== null ? { text: formatMessage({ id: 'sxVideoTotal' }, { p: e.totalPercent }) } : null,
                ])
            case 'TEST':
                return keep([
                    e.point !== null ? { text: e.maxPoint ? `${e.point} / ${e.maxPoint}` : String(e.point) } : null,
                    e.attempt ? { text: formatMessage({ id: 'sxAttempt' }, { n: e.attempt }) } : null,
                    e.attempt ? { text: formatMessage({ id: e.passed ? 'sxPassed' : 'sxNotPassed' }), tone: e.passed ? 'ok' : 'bad' } : null,
                ])
            case 'CONSPECT':
            case 'HOMEWORK':
                return keep([
                    e.checked
                        ? { text: formatMessage({ id: 'sxChecked' }), tone: 'ok' }
                        : { text: formatMessage({ id: 'sxNotChecked' }), tone: 'warn' },
                    e.point !== null ? { text: formatMessage({ id: 'sxPoint' }, { p: e.point }) } : null,
                ])
            case 'PRACTICE':
                return [
                    { text: `${formatMessage({ id: 'practice1' })}: ${practice(e.status)}`, tone: e.status === 'ABSENT' ? 'bad' : undefined },
                    { text: `${formatMessage({ id: 'practice2' })}: ${practice(e.status2)}`, tone: e.status2 === 'ABSENT' ? 'bad' : undefined },
                ]
            case 'ZACHET':
                return e.point !== null ? [{ text: formatMessage({ id: 'sxPoint' }, { p: e.point }) }] : []
            default:
                return []
        }
    }

    return (
        <Box sx={styles.card}>
            <Box sx={styles.head}>
                <Box sx={{ color: ui.ink, fontSize: '16px', fontWeight: 600 }}>{formatMessage({ id: 'sxTitle' })}</Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginLeft: 'auto' }}>
                    {PERIODS.map((p) => (
                        <Box key={p} component="button" type="button" onClick={() => setDays(p)} sx={styles.pill(p === days)}>
                            {formatMessage({ id: 'sxDaysN' }, { n: p })}
                        </Box>
                    ))}
                </Box>
            </Box>

            {data && (
                <Box sx={styles.summary}>
                    <Box sx={{ color: ui.sub, fontSize: '14px' }}>
                        {formatMessage({ id: 'sxLastSeen' })}
                        {': '}
                        <Box component="span" sx={{ color: ui.ink, fontWeight: 600 }}>
                            {data.lastSeenAt
                                ? `${formatDate(data.lastSeenAt)} ${formatTime(data.lastSeenAt)} · ${ago(idle ?? 0)}`
                                : formatMessage({ id: 'sxNever' })}
                        </Box>
                    </Box>
                    <Box sx={{ color: ui.sub, fontSize: '14px' }}>
                        {formatMessage({ id: 'sxActiveDays' }, { n: data.activeDays, total: data.days.length })}
                    </Box>
                    {alert && (
                        <Box component="span" sx={chip(tone.bad)}>
                            {idle === null ? formatMessage({ id: 'sxNever' }) : formatMessage({ id: 'sxIdle' }, { n: idle })}
                        </Box>
                    )}
                </Box>
            )}
            {data && (
                <Box sx={{ color: ui.muted, fontSize: '12.5px' }}>
                    {data.trackedSince
                        ? formatMessage({ id: 'sxTrackedSince' }, { date: formatDate(data.trackedSince) })
                        : formatMessage({ id: 'sxNotTracked' })}
                </Box>
            )}

            {isFetching && <LinearProgress />}

            {data && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {data.days.map((d) => (
                        <Box key={d.day} sx={styles.day}>
                            <Box sx={styles.dayHead}>
                                <Box sx={{ color: ui.ink, fontWeight: 600, fontSize: '14px', minWidth: 92 }}>
                                    {formatDate(d.day).slice(0, 5)}{' '}
                                    <Box component="span" sx={{ color: ui.muted, fontWeight: 400 }}>{weekday(d.day)}</Box>
                                </Box>
                                <Box component="span" sx={chip(d.loggedIn ? tone.ok : tone.neutral)}>
                                    {formatMessage({ id: d.loggedIn ? 'sxLoggedIn' : 'sxNotLoggedIn' })}
                                </Box>
                                {d.loggedIn && !d.events.length && (
                                    <Box sx={{ color: ui.muted, fontSize: '13px' }}>{formatMessage({ id: 'sxNoActions' })}</Box>
                                )}
                            </Box>
                            {d.events.map((e, i) => (
                                <Box key={i} sx={styles.event}>
                                    <Box sx={{ color: ui.muted, fontSize: '12.5px', minWidth: 40 }}>{formatTime(e.at)}</Box>
                                    <Box component="span" sx={chip(tone.info)}>{TYPE_LABEL[e.type] ? formatMessage({ id: TYPE_LABEL[e.type] }) : e.type}</Box>
                                    <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
                                        <Box sx={{ color: ui.ink, fontSize: '14px', overflowWrap: 'anywhere' }}>
                                            {e.title || week(e)}
                                        </Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', fontSize: '12.5px', color: ui.sub }}>
                                            {subjectOf(e) && <span>{subjectOf(e)}</span>}
                                            {e.title && week(e) && <span>{week(e)}</span>}
                                            {details(e).map((x, j) => (
                                                <span key={j} style={x.tone ? { color: tone[x.tone].text, fontWeight: 600 } : undefined}>{x.text}</span>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

export default StudentActivityBlock

const styles = {
    card: {
        gridColumn: '1 / -1',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
        padding: '16px 20px',
        borderRadius: '16px',
        border: `1px solid ${ui.border}`,
        backgroundColor: ui.card,
        minWidth: 0,
    },
    head: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '8px 12px',
    },
    summary: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '6px 20px',
    },
    day: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '6px',
        padding: '10px 0',
        borderTop: `1px solid ${ui.border}`,
    },
    dayHead: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '8px 12px',
    },
    event: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'flex-start',
        gap: '6px 10px',
        padding: '6px 10px',
        borderRadius: '10px',
        backgroundColor: ui.surface,
        minWidth: 0,
    },
    pill: (active: boolean) => ({
        cursor: 'pointer',
        font: 'inherit',
        fontSize: '13px',
        lineHeight: 1.2,
        padding: '6px 12px',
        borderRadius: '12px',
        whiteSpace: 'nowrap' as const,
        background: active ? ui.pinkSoft : 'transparent',
        border: `1px solid ${active ? 'transparent' : ui.border}`,
        color: active ? ui.pink : ui.sub,
        fontWeight: active ? 600 : 400,
        '&:hover': { borderColor: ui.pink, color: ui.pink },
    }),
}
